import { useTheme } from '@emotion/react'
import { CloseOutlined } from '@mui/icons-material'
import { Box, Dialog, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import Chip from 'components/Chip'
import CircleProgressbarWithInfo from 'components/CircleProgressbar'
import CustomButton from 'components/CustomButton'
import { FileUploadSection } from 'components/FileUploadSection'
import { useServices } from 'contexts'
import { useNavigateWithClient } from 'hooks'
import React from 'react'
import { useState, useEffect } from 'react'
import { createResumeRecord } from 'services'
import { analyseResume, submitResumeScore } from 'services/shortlistingService'
import { Session } from 'utils'

const useStyles = makeStyles((theme) => ({
  container : {
    display : 'flex',
    flexDirection : 'column',
    padding : theme.spacing(10),
    gap : theme.spacing(8),
    height : '100%',
    width : '100%'
  },
  uploadContainer : {
    display : 'flex',
    flexDirection : 'column',
    alignItems : 'center',
    gap : theme.spacing(5),
    width : '100%',
    height : '180px',
    borderRadius : theme.spacing(4)
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    height: '100%',
    width: '100%',
    padding: theme.spacing(10),
    position : 'relative',
    overflow : 'hidden'
  },
  circularGifBot: {
    position: 'absolute',
    left: '-150px',
    bottom : '-135px',
    opacity: '10%'
  },
  circularGifTop: {
    position: 'absolute',
    right: '-150px',
    top : '-135px',
    opacity: '10%'
  },
}))

const ShortlistingTaskSteps = {
  Upload_Resume : {
    _id : 'Upload Resume'
  },
  Analyzing_Resume : {
    _id : 'Analyzing Resume',
  },
  Feedback : {
    _id : 'Feedback'
  },
  Success : {
    _id : 'Success'
  }
}

const ShortlistingTask = ({openDialog , onClose = () => {} , task}) => {
  const classes = useStyles();
  const theme = useTheme();
  const service = useServices();
  const navigateWithClient = useNavigateWithClient();

  const [uploadedFiles , setUploadedFiles] = useState();
  const [uploading , setUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(ShortlistingTaskSteps.Upload_Resume._id);
  const [countdown, setCountdown] = useState(3);
  const [resumeId , setResumeId] = useState('');
  const [relevanceScore , setRelevenceScore] = useState();
  const [relevanceId , setRelevanceId] = useState(null)

  const resetStates = () => {
    setUploadedFiles(undefined);
    setUploading(false);
    setCurrentStep(ShortlistingTaskSteps.Upload_Resume._id);
    setCountdown(3);
    setResumeId('');
    setRelevenceScore(undefined);
    setRelevanceId(null);
  };

  useEffect(() => {
    if (openDialog) {
      resetStates();
    }
  }, [openDialog]);

  const handleFileProcessed = async({ fileName, scrapedData}) => {
    try {
      setUploading(true)
      let payload = {
        user : Session.userId,
        name : fileName,
        parsedData : scrapedData.extractedText,
        path : scrapedData.filePath
      };
      if(scrapedData){
        let resumeRecord = await service.callService(createResumeRecord, payload)
        setResumeId(resumeRecord._id)
      }
    } catch (error) {
      console.error('Error processing file:', error);
    } 
  };

  const renderSuccessStep = () => (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
      padding: theme.spacing(10),
      gap: theme.spacing(4)
    }}>
      {/* Success Icon */}
      <Box sx={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        border: '3px solid #10B981',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white'
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path 
            d="M20 6L9 17L4 12" 
            stroke="#10B981" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </Box>

      {/* Success Message */}
      <Typography 
        variant='h6' 
        sx={{ 
          color: theme.palette.neutral['clr-900'],
          textAlign: 'center',
          fontWeight: 500,
          fontSize: '18px'
        }}
      >
        Relevance score submitted successfully!
      </Typography>

      {/* Countdown Timer */}
      <Typography 
        variant='body1' 
        sx={{ 
          color: theme.palette.neutral['clr-500'],
          textAlign: 'center'
        }}
      >
        Closing in {countdown} seconds...
      </Typography>
    </Box>
  );

  const handleCheckRelevanceScore = async() => {
    setCurrentStep(ShortlistingTaskSteps.Analyzing_Resume._id);

    let payload = {
      user : Session.userId,
      resume : resumeId,
      jobRole : task?.jobRole,
      shortlistTask: task?._id,
      jobDescription: task?.jobDescription,
      company: task?.company,
    }

    let result = await service.callService(analyseResume, payload)
    
    if(result){
      setRelevenceScore(result.score)
      setRelevanceId(result._id)
      setCurrentStep(ShortlistingTaskSteps.Feedback._id);
      
      const currentValue = parseInt(localStorage.getItem('resumeAnalyseCount')) || 0;
      localStorage.setItem('resumeAnalyseCount', (currentValue + 1).toString());
      
    }
  };

  const handleSubmitScore = async() => {
    setCurrentStep(ShortlistingTaskSteps.Success._id);
    await service.callService(submitResumeScore, relevanceId)
    setCountdown(3);
    
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const renderUploadResumeStep = () => (
    <Box className={classes.container}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='h5-medium' color='#1151A6'>Shortlisting task</Typography>
        <Box display='flex' gap='12px' alignItems='center'>
           {/* <Chip
            content='Credits: 02'
            color={theme.palette.primary.main}
            bgColor={theme.palette.primary['clr-50']}
            sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
          /> */}
        </Box>
      </Box>
      <Box display='flex' flexDirection='column' gap='8px'>
        <Typography
          variant='h6-medium'
          noWrap
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: '100%' 
          }}
        >
          {task?.name}
        </Typography>

        <Chip
            content={task?.company ? 'Company specific' : 'Job role specific'}
            color={theme.palette.neutral['clr-600']}
            bgColor={theme.palette.neutral['clr-100']}
            // disabled={disabled}
            sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
          />
      </Box>
      <Box display='flex' flexDirection='column' gap='12px'>
        <Typography variant='h6-semiBold' color='neutral.clr-600'>
          Upload resume and submit relevance score
        </Typography>
        <Box 
          className={classes.uploadContainer} 
          sx={{ 
            border : uploading ? 'none' : `1px dotted ${theme.palette.neutral['clr-200']}`,
            justifyContent : uploading ? 'flex-start' : 'center'
          }}
          >
          {!uploading && (
            <Box display='flex' flexDirection='column' gap='8px'>
              <Typography variant='h6-medium' color='neutral.clr-500' sx={{textAlign : 'center'}}>
                Choose a file or drag & drop it here
              </Typography>
              <Typography variant='body01-medium' color='neutral.clr-500' sx={{textAlign : 'center'}}>
                PDF only
              </Typography>
            </Box>
          )}
          
          <FileUploadSection
            fileType="resume"
            iconSrc="https://assets.languify.in/images/resumeDoc.png"
            onFileProcessed={handleFileProcessed}
            uploadName="resume"
            changeText="job description"
            width='440px'
            showDate={true}
            uploadToS3={true}
          />

          {uploading && (
            <Box display='flex' flexDirection='column' gap='8px'>
              <CustomButton
                sx={{width : '520px'}}
                onClick={handleCheckRelevanceScore}
              >
                Check Relevance Score
              </CustomButton>
              {/* <Typography variant='body01-medium' color='neutral.clr-800' sx={{textAlign : 'center'}}>
              1 credit will be used to check score.
              </Typography> */}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderAnalyzingStep = () => (
    <Box className={classes.dialogContainer}>
      <img
        src='https://assets.languify.in/images/aiAssessment.gif'
        alt='gif'
        width={108}
        height={108}
      />
       <img
          src='https://assets.languify.in/images/circlularGifPersonalized.gif'
          alt='gif'
          width={298}
          height={298}
          className={classes.circularGifBot}
        />
      <img
        src='https://assets.languify.in/images/circlularGifPersonalized.gif'
        alt='gif'
        width={298}
        height={298}
        className={classes.circularGifTop}
      />
      <Typography 
        variant='h6-medium' 
        color='neutral.clr-900' 
        sx={{maxWidth : '342px' , textAlign : 'center'}}
      >
        Analyzing how well your resume matches the job role…
      </Typography>
    </Box>
  );

  const renderFeedbackStep = () => {
    
    // Determine color and message based on score
    const getScoreConfig = (score) => {
      if (score >= 70) {
        return {
          color: '#10B981', // Green
          bgColor: '#ECFDF5', // Light green
          emoji: '🏌️',
          message: 'A higher relevance score improves your chances of getting shortlisted'
        };
      } else if (score >= 40) {
        return {
          color: '#F59E0B', // Orange
          bgColor: '#FEF3C7', // Light orange
          emoji: '😔',
          message: 'Go through the feedback and make changes so that your resume has a higher chance of getting shortlisted.'
        };
      } else {
        return {
          color: '#EF4444', // Red
          bgColor: '#FEF2F2', // Light red
          emoji: '😣',
          message: 'Go through the feedback and make changes so that your resume has a higher chance of getting shortlisted.'
        };
      }
    };

    const scoreConfig = getScoreConfig(relevanceScore);

    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(10),
        gap: theme.spacing(6),
        height: '100%',
        width: '100%'
      }}>

        {/* Main content area */}
        <Box display='flex' justifyContent='center' alignItems='center' gap={theme.spacing(6)}>
          <CircleProgressbarWithInfo
            fontVariant="h4-semiBold"
            size={164}
            value={relevanceScore}
            category='score'
            label='Relevance score'
          />

          {/* Message Box */}
          <Box sx={{
            backgroundColor: scoreConfig.bgColor,
            borderRadius: theme.spacing(2),
            padding: theme.spacing(4),
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2)
          }}>
            <Typography sx={{ fontSize: '32px' }}>
              {scoreConfig.emoji}
            </Typography>
            <Typography 
              variant='body1' 
              sx={{ 
                color: theme.palette.neutral['clr-700'],
                textAlign: 'center',
                lineHeight: 1.5
              }}
            >
              {scoreConfig.message}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box display='flex' flexDirection='column' gap={theme.spacing(2)}>
          <CustomButton
            onClick={handleSubmitScore}
            sx={{width: '100%'}}
            variant='outlined'
          >
            Submit Score
          </CustomButton>
          <CustomButton
            sx={{ width: '100%' }}
            onClick={() => navigateWithClient(`/performance/shortlist-genie/${relevanceId}`)}
          >
            View feedback & improve Score
          </CustomButton>
        </Box>
      </Box>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case ShortlistingTaskSteps.Upload_Resume._id:
        return renderUploadResumeStep();
      case ShortlistingTaskSteps.Analyzing_Resume._id:
        return renderAnalyzingStep();
      case ShortlistingTaskSteps.Feedback._id:
        return renderFeedbackStep();
      case ShortlistingTaskSteps.Success._id:
        return renderSuccessStep();
      default:
        return renderUploadResumeStep();
    }
  };

  const handleClose = () => {
    resetStates(); 
    onClose(); 
  };

  const showCloseButton = currentStep !== ShortlistingTaskSteps.Analyzing_Resume._id;

  const isDialogClosable = currentStep !== ShortlistingTaskSteps.Analyzing_Resume._id;

  return (
    <Dialog
        open={openDialog}
        onClose={isDialogClosable ? handleClose : undefined}
        disableEscapeKeyDown={!isDialogClosable}
        sx={{
          '& .MuiDialog-paper': {
            width: '720px', 
            height: '440px',
          },
          '& .MuiBackdrop-root': {
             backgroundColor: 'rgba(0, 17, 38, 0.7)',
             backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
          },
        }}
      >
        {showCloseButton && (
          <Box sx={{ position: 'absolute', top: theme.spacing(3), right: theme.spacing(3) }}>
            <CloseOutlined 
              onClick={handleClose} 
              style={{ cursor: 'pointer', color: theme.palette.neutral['clr-500'] }} 
            />
          </Box>
        )}
        {renderCurrentStep()}
    </Dialog>
  )
}

export default ShortlistingTask